<template>
  <v-layout column>
    <v-flex>
      <v-layout>
        <v-flex xs7 offset-xs5>
          <v-layout class="pl-1">
            <v-flex shrink align-center style="display: flex;">
              <StatusSelect
                v-model="optionForShowCreatives"
                style="width: 180px;"
              />
              <v-flex shrink d-flex align-center>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      :disabled="!reloadable"
                      :loading="loading"
                      @click="removeAllCreatives"
                      small
                      icon
                    >
                      <v-icon small>close</v-icon>
                    </v-btn>
                  </template>
                  <span>全てのクリエイティブ紐付きを削除する</span>
                </v-tooltip>
              </v-flex>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex>
      <v-layout>
        <v-flex xs5>
          <v-subheader>配信地点グループ</v-subheader>
          <AppScrollContainer :height="height">
            <DeliveryLocationGroupTable
              ref="deliveryLocationGroupTable"
              v-model="currentDeliveryLocationGroup"
              :items="menuSetting.visibleDeliveryLocationGroups"
              :form="form"
            />
          </AppScrollContainer>
        </v-flex>
        <v-divider vertical />
        <v-flex xs7>
          <v-subheader>配信クリエイティブ</v-subheader>
          <AppScrollContainer :height="height">
            <div :class="fabContainerStyles">
              <CreativeTable
                v-model="currentCreative"
                :items="visibleCreatives"
                :loading="loading"
                @select="currentCreative = $event"
              >
                <template v-slot:empty>
                  <span v-if="currentLocationGroup">
                    <v-icon small>add</v-icon>
                    ボタンを押してクリエイティブを設定してください
                  </span>
                  <span v-else>表示する配信地点グループを選択してください</span>
                </template>
              </CreativeTable>
              <CreativeButtonAdd
                v-if="currentLocationGroup"
                @onClick="showModal = true"
                :icon="editIcon"
              />
              <ModalEdit
                v-if="currentDeliveryLocationGroup && showModal"
                v-model="showModal"
                :delivery-location-group="currentDeliveryLocationGroup"
                :resources="resources"
                :creative-type="form.campaign.creativeType"
                @apply="onChangeCreatives"
              />
            </div>
          </AppScrollContainer>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex>
      <Preview ref="preview" :current-creative="currentCreative" />
    </v-flex>
  </v-layout>
</template>

<script>
import { css } from 'emotion'
import DeliveryLocationGroupTable from './CampaignModalPageCreativeDeliveryLocationGroupTable'
import CreativeTable from './CampaignModalPageCreativeCreativeTable'
import Preview from './CampaignModalPageCreativePreview'
import ModalEdit from './CampaignModalPageCreativeModeDeliveryGroupModalEdit'
import CreativeButtonAdd from './CampaignModalPageCreativeButtonAdd'
import { CreativeRepository } from '@/models/Creative'
import StatusSelect, {
  StatusOptions,
} from './CampaignModalPageCreativeStatusSelect'

export default {
  name: 'CampaignModalPageCreativeModeDeliveryGroup',
  components: {
    Preview,
    DeliveryLocationGroupTable,
    CreativeTable,
    CreativeButtonAdd,
    ModalEdit,
    StatusSelect,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      creatives: [], // 右カラムにクリエイティブリストを表示するための配列
      currentCreative: null,
      currentDeliveryLocationGroup: null,
      height: 300,
      loading: false,
      showModal: false,
      optionForShowCreatives: StatusOptions.ACTIVE_ONLY,
    }
  },
  computed: {
    menuSetting() {
      return this.form.campaign.menuSetting
    },
    deliveryLocationGroups() {
      return this.menuSetting.deliveryLocationGroups.map(g =>
        this.menuSetting.findLocationGroup(g.locationGroupId),
      )
    },
    currentLocationGroup: {
      get() {
        return (
          this.currentDeliveryLocationGroup &&
          this.menuSetting.findLocationGroup(
            this.currentDeliveryLocationGroup.locationGroupId,
          )
        )
      },
      set(locationGroup) {
        this.currentDeliveryLocationGroup = this.menuSetting.deliveryLocationGroups.find(
          g => g.locationGroupId === locationGroup.id,
        )
      },
    },
    fabContainerStyles() {
      return css`
        position: relative;
        height: 100%;
      `
    },
    editIcon() {
      return this.creatives.length > 0 ? 'edit' : 'add'
    },
    resources() {
      return [this.form.advertiser, this.form.insertionOrder]
    },
    reloadable() {
      return this.visibleCreatives.length > 0 || this.currentCreative !== null
    },
    visibleCreatives: {
      get() {
        if (this.optionForShowCreatives === StatusOptions.ALL) {
          return this.creatives
        } else {
          return this.creatives.filter(c => c.isActive)
        }
      },
      set(values) {
        this.creatives = values
      },
    },
    confirmMessage() {
      const names = this.menuSetting.deliveryLocationGroups
        .filter(g => g.creatives.length === 0)
        .map(g => this.menuSetting.findLocationGroup(g.locationGroupId).name)
      return names.length
        ? `配信地点グループ${names.join(
            '、',
          )}にクリエイティブが設定されていないためこの地点グループには配信されません。`
        : ''
    },
    isGeoMenuSetting() {
      return this.form.campaign.hasGeoMenuSetting
    },
  },
  watch: {
    currentDeliveryLocationGroup() {
      if (this.currentDeliveryLocationGroup) {
        this.fetchCreatives()
      }
      this.checkDeliveryLocationGroups()
    },
  },
  created() {
    if (this.menuSetting.deliveryLocationGroups.length === 1) {
      const [group] = this.menuSetting.deliveryLocationGroups
      this.currentDeliveryLocationGroup = group
    }
  },
  methods: {
    async fetchCreatives() {
      if (!this.currentDeliveryLocationGroup) return
      try {
        this.loading = true
        const result = await CreativeRepository.findByIdsWithFolderInfo(
          this.currentDeliveryLocationGroup.creatives,
        )
        this.creatives = result
      } catch (e) {
        console.error(e)
        throw e
      } finally {
        this.loading = false
      }
    },
    async checkDeliveryLocationGroups() {
      this.$refs.deliveryLocationGroupTable.check()
    },
    reloadCreatives() {
      this.fetchCreatives()
      this.checkDeliveryLocationGroups()
      this.$refs.preview.reload()
    },
    onChangeCreatives() {
      this.fetchCreatives()
      this.checkDeliveryLocationGroups()
    },
    removeAllCreatives() {
      this.form.campaign.removeCreatives()
      if (this.isGeoMenuSetting) {
        this.menuSetting.removeDeliveryCreatives()
      }
      this.currentCreative = null
      this.visibleCreatives = []
    },
  },
}
</script>

<style></style>
